import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import { PageHeader } from "../components/Layout";
import { useToast } from "../components/Toast";
import {
  Badge,
  ConfirmModal,
  EmptyState,
  Field,
  IconCalendar,
  IconEdit,
  IconExternal,
  IconPin,
  IconPlus,
  IconTrash,
  IconUsers,
  Modal,
  Spinner,
  Switch,
} from "../components/ui";
import { dateParts, formatDateTime, toLocalInputValue } from "../utils/format";
import EventRsvps from "./EventRsvps";

export default function Events() {
  const toast = useToast();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [viewingRsvps, setViewingRsvps] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { events: list } = await api.get("/events");
      setEvents(list);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toast identity is stable
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function togglePublished(event) {
    try {
      const { event: updated } = await api.post(`/events/${event.id}/toggle-published`);
      setEvents((list) =>
        list.map((e) => (e.id === updated.id ? { ...e, is_published: updated.is_published } : e))
      );
      toast.success(
        updated.is_published ? `"${updated.title}" is now live on the site.` : `"${updated.title}" unpublished.`
      );
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function deleteEvent(event) {
    setBusy(true);
    try {
      await api.del(`/events/${event.id}`);
      toast.success(`Deleted "${event.title}".`);
      setConfirming(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  const published = events.filter((e) => e.is_published).length;

  return (
    <>
      <PageHeader
        title="Events"
        subtitle={loading ? "Loading…" : `${events.length} total · ${published} live on the site`}
      >
        <button type="button" className="btn btn-sm btn-primary" onClick={() => setCreating(true)}>
          <IconPlus />
          New event
        </button>
      </PageHeader>

      <div className="content">
        <div className="panel">
          {loading ? (
            <div style={{ padding: 22 }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <div className="row" key={i} style={{ padding: "14px 0", gap: 16 }}>
                  <div className="skeleton" style={{ width: 54, height: 50, borderRadius: 12 }} />
                  <div style={{ flex: 1 }}>
                    <div className="skeleton" style={{ width: "35%", marginBottom: 7 }} />
                    <div className="skeleton" style={{ width: "55%", height: 10 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : !events.length ? (
            <EmptyState
              icon={IconCalendar}
              title="No events yet"
              message="Create a gathering, then publish it to show it on the public site with an RSVP button."
              action={
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
                  <IconPlus />
                  Create the first event
                </button>
              }
            />
          ) : (
            events.map((e) => {
              const parts = dateParts(e.event_date);
              return (
                <div className="event-row" key={e.id}>
                  <div className={`event-date-box ${parts?.isPast ? "is-past" : ""}`}>
                    {parts ? (
                      <>
                        <span className="d">{parts.day}</span>
                        <span className="m">{parts.month}</span>
                      </>
                    ) : (
                      <span className="no-date">TBC</span>
                    )}
                  </div>

                  <div className="event-row-body">
                    <h4>{e.title}</h4>
                    <div className="event-row-meta">
                      <span>
                        <IconCalendar />
                        {formatDateTime(e.event_date)}
                      </span>
                      <span>
                        <IconPin />
                        {e.location || "Location TBC"}
                      </span>
                      {e.rsvp_url ? (
                        <span>
                          <IconExternal />
                          External RSVP
                        </span>
                      ) : (
                        <span>
                          <IconUsers />
                          {e.rsvp_count} RSVP{e.rsvp_count === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="event-row-actions">
                    {parts?.isPast && <Badge>Past</Badge>}
                    {e.is_published ? (
                      <Badge tone="green">
                        <span className="badge-dot" />
                        Live
                      </Badge>
                    ) : (
                      <Badge tone="amber">Draft</Badge>
                    )}

                    <Switch checked={e.is_published} onChange={() => togglePublished(e)} />

                    {!e.rsvp_url && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setViewingRsvps(e)}
                        disabled={!e.rsvp_count}
                        title={e.rsvp_count ? "View RSVPs" : "No RSVPs yet"}
                      >
                        <IconUsers />
                        RSVPs
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-icon btn-ghost"
                      onClick={() => setEditing(e)}
                      title="Edit"
                    >
                      <IconEdit />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-icon btn-ghost"
                      onClick={() => setConfirming(e)}
                      title="Delete"
                    >
                      <IconTrash style={{ color: "var(--red)" }} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {(creating || editing) && (
        <EventModal
          event={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(saved, wasNew) => {
            setCreating(false);
            setEditing(null);
            toast.success(wasNew ? `"${saved.title}" created.` : `"${saved.title}" updated.`);
            load();
          }}
        />
      )}

      {viewingRsvps && <EventRsvps event={viewingRsvps} onClose={() => setViewingRsvps(null)} />}

      {confirming && (
        <ConfirmModal
          title={`Delete "${confirming.title}"?`}
          message={
            confirming.rsvp_count
              ? `This also deletes its ${confirming.rsvp_count} RSVP${
                  confirming.rsvp_count === 1 ? "" : "s"
                }. Export them first if you need the list.`
              : "This permanently removes the event."
          }
          onConfirm={() => deleteEvent(confirming)}
          onClose={() => setConfirming(null)}
          busy={busy}
        />
      )}
    </>
  );
}

function EventModal({ event, onClose, onSaved }) {
  const isNew = !event;
  const [form, setForm] = useState(() => ({
    title: event?.title || "",
    description: event?.description || "",
    event_date: toLocalInputValue(event?.event_date),
    location: event?.location || "",
    rsvp_url: event?.rsvp_url || "",
    is_published: event?.is_published || false,
  }));
  const [fields, setFields] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setFields({});

    const payload = {
      ...form,
      // datetime-local gives "YYYY-MM-DDTHH:mm" with no zone; send a real
      // instant so the server stores the admin's intended local time.
      event_date: form.event_date ? new Date(form.event_date).toISOString() : null,
    };

    try {
      const res = isNew ? await api.post("/events", payload) : await api.patch(`/events/${event.id}`, payload);
      onSaved(res.event, isNew);
    } catch (err) {
      setError(err.message);
      setFields(err.fields || {});
      setBusy(false);
    }
  }

  return (
    <Modal
      title={isNew ? "New event" : `Edit "${event.title}"`}
      subtitle="Published events appear on the public site straight away."
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="event-form" className="btn btn-primary" disabled={busy}>
            {busy && <Spinner />}
            {isNew ? "Create event" : "Save changes"}
          </button>
        </>
      }
    >
      <form id="event-form" onSubmit={submit} noValidate>
        {error && (
          <div className="login-alert" style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}

        <Field label="Title *" error={fields.title}>
          <input
            className={`input ${fields.title ? "has-error" : ""}`}
            value={form.title}
            onChange={set("title")}
            placeholder="e.g. HR Leads Hangout — DIFC"
            required
          />
        </Field>

        <div className="field-grid">
          <Field label="Date & time" error={fields.event_date} hint="Leave the time at 00:00 to hide it publicly.">
            <input
              className={`input ${fields.event_date ? "has-error" : ""}`}
              type="datetime-local"
              value={form.event_date}
              onChange={set("event_date")}
            />
          </Field>
          <Field label="Location" error={fields.location}>
            <input className="input" value={form.location} onChange={set("location")} placeholder="e.g. DIFC, Dubai" />
          </Field>
        </div>

        <Field label="Description" error={fields.description}>
          <textarea
            className="textarea"
            value={form.description}
            onChange={set("description")}
            rows={3}
            placeholder="What's the evening about? Who should come?"
          />
        </Field>

        <Field
          label="External RSVP link"
          error={fields.rsvp_url}
          hint="Leave blank to use the built-in RSVP form — then RSVPs are collected here and added to your members list."
        >
          <input
            className={`input ${fields.rsvp_url ? "has-error" : ""}`}
            type="url"
            value={form.rsvp_url}
            onChange={set("rsvp_url")}
            placeholder="https://lu.ma/… (optional)"
          />
        </Field>

        <div style={{ marginTop: 4 }}>
          <Switch
            checked={form.is_published}
            onChange={(v) => setForm((f) => ({ ...f, is_published: v }))}
            label="Publish on the public site"
          />
        </div>
      </form>
    </Modal>
  );
}
